<template>
    <div class="directions">
        <div class="row route-chooser mb-4" v-if="design !== 'homepage'">
            <div
                class="col-12 mb-mobile-1"
                :class="{
                    'col-md-6': status.hasRouteBack,
                }"
            >
                <b class="mb-3 d-inline-block">{{ $t('directions.trip') }}:</b>
                <div class="routes">
                    <button
                        type="button"
                        class="mb-3 shop-product-card navigator-card bg-white p-4 rounded w-100 text-left"
                        v-for="route in routes.outward"
                        :class="{
                            highlighted: route.id === activeRoute,
                        }"
                        :key="'route_outward_' + route.id"
                        @click.prevent="$emit('select-route', route)"
                    >
                        <div class="text-primary">
                            <i class="mdi mdi-map-marker"></i> {{ route.summary }}
                            <i class="mdi mdi-clock-fast ml-4"></i> {{ route.duration.text }}
                        </div>
                        <div class="text-quote">
                            {{ route.translatedCountryChanges.join(' → ') }}
                        </div>
                    </button>
                </div>
            </div>
            <div v-if="status.hasRouteBack" class="col-12 col-md-6">
                <b class="mb-3 d-inline-block">{{ $t('directions.trip_back') }}:</b>
                <div class="routes">
                    <button
                        type="button"
                        class="mb-3 shop-product-card navigator-card bg-white p-4 rounded w-100 text-left"
                        v-for="route in routes.back"
                        :class="{
                            highlighted: route.id === activeBackRoute,
                        }"
                        :key="'route_back_' + route.id"
                        @click.prevent="$emit('select-route-back', route)"
                    >
                        <div class="text-primary">
                            <i class="mdi mdi-map-marker"></i> {{ route.summary }}
                            <i class="mdi mdi-clock-fast ml-4"></i> {{ route.duration.text }}
                        </div>
                        <div class="text-quote">
                            {{ route.translatedCountryChanges.join(' → ') }}
                        </div>
                    </button>
                </div>
            </div>
        </div>

        <div
            class="mb-2"
            v-if="showCalendarAsResult"
            v-html="$t('directions.productconfigurator_routeplanner_additional_text')"
        ></div>

        <template v-if="showCalendarAsResult">
            <Calendar
                :select-systems="preselectRequiredSystems"
                :allowed-to-add="true"
                :allowed-to-remove="true"
                :max-validity-start="maxValidityStart"
                :max-validity-end="maxValidityEnd"
            ></Calendar>
        </template>
        <template v-else>
            <div class="row" v-if="design === 'homepage'">
                <strong class="col-12" v-html="$t('directions.travelRoute')"></strong>
            </div>

            <div class="row mb-5" v-if="design === 'homepage'">
                <div class="col-12 mt-4">
                    <div class="routes">
                        <button
                            type="button"
                            class="mb-3 shop-product-card navigator-card bg-white p-4 rounded w-100 text-left"
                            v-for="route in firstOutwardRoute"
                            :class="{
                                highlighted: route.id === activeRoute,
                            }"
                            :key="'route_outward_' + route.id"
                            @click.prevent="$emit('select-route', route)"
                        >
                            <div class="text-primary">
                                <i class="mdi mdi-map-marker"></i> {{ route.summary }}
                                <i class="mdi mdi-clock-fast ml-4"></i> {{ route.duration.text }}
                            </div>
                            <div class="text-quote">
                                {{ route.translatedCountryChanges.join(' → ') }}
                            </div>
                        </button>
                    </div>
                </div>
            </div>

            <div class="row">
                <strong v-if="design !== 'homepage'" class="col-12" v-html="$t('directions.productsRequired')"></strong>
                <strong
                    v-else-if="design === 'homepage'"
                    class="col-12"
                    v-html="$t('directions.productsRequired_homepage')"
                ></strong>
            </div>

            <div class="row" v-if="this.requiredProducts.length > 0">
                <Product v-for="product in requiredProducts" :key="product.id" :design="design" :item="product" />
            </div>
            <div class="row" v-else>
                <div class="col-12 mt-3">
                    {{ $t('directions.nothing_required') }}
                </div>
            </div>

            <template v-if="requireMultipleCountries">
                <div class="row mt-4">
                    <strong class="col-12" v-html="$t('directions.kombivignetteTitle')"></strong>
                </div>
                <div class="row mt-4">
                    <div class="col-12 col-md-6">
                        <a
                            class="directions-product-listing shop-product-card navigator-card bg-white p-3 rounded w-100 text-left"
                            :href="routeToCombinedOrder"
                            target="_blank"
                        >
                            <div class="row w-100 directions-align-center">
                                <div class="col-12 col-lg-8">
                                    <div class="row directions-align-center">
                                        <div class="col-3 text-left mobile-shop-adverts-text">
                                            <img
                                                class="directions-flag"
                                                width="50"
                                                height="auto"
                                                src="/assets/img/svg/europe.svg"
                                                alt=""
                                            />
                                        </div>
                                        <div class="col-9 text-left">
                                            <div
                                                class="text-quote mobile-shop-adverts-text"
                                                v-html="$t('shopEuropamaut.productName')"
                                            ></div>
                                        </div>
                                    </div>
                                </div>
                                <div class="col-12 col-lg-4 mobile-shop-advert-text-hidden">
                                    <div class="call-to-action-advert-container">
                                        <strong class="d-block" v-html="$t('directions.orderNow')"></strong>
                                    </div>
                                </div>
                            </div>
                        </a>
                    </div>
                </div>
            </template>

            <template v-if="infos.length > 0">
                <div class="row mt-4">
                    <strong class="col-12" v-html="$t('directions.notImplementedInfoTitle')"></strong>
                </div>
                <div class="row mt-4">
                    <div
                        v-for="(info, index) in infos"
                        :key="JSON.stringify(info)"
                        class="col-12 col-md-6"
                        :class="{
                            'mb-3': index !== infos.length - 1,
                        }"
                    >
                        <a
                            v-if="info.asLink"
                            :href="info.content"
                            target="_blank"
                            rel="noopener nofollow"
                            class="directions-product-listing direction-info navigator-card bg-white p-3 rounded w-100 text-left"
                        >
                            <strong>{{ info.country.label }}</strong
                            >:&nbsp;
                            <span v-html="$t('directions.notImplementedGetInfos')"></span>
                        </a>
                        <div
                            v-else
                            class="directions-product-listing direction-info navigator-card bg-white p-3 rounded w-100 text-left"
                            v-html="info.content"
                        ></div>
                    </div>
                </div>
            </template>

            <h2 v-if="routeplanerShowProducts === true" class="mt-lg-5 mt-mobile-3">
                {{ $t('frontend.routeplaner_homepage_further_products') }}
            </h2>
        </template>
    </div>
</template>

<style>
.direction-info {
    border: 1px solid #d2d2d7;
    align-items: center;
    min-height: unset;
    color: var(--main-color-gray);
}
</style>

<script>
import Calendar from '../ProductConfigurator/Calendar/Calendar.vue';
import Product from './Product.vue';

export default {
    components: {
        Calendar,
        Product,
    },

    computed: {
        routeToCombinedOrder() {
            const vignetteParameters = [];
            this.products.vignettes.forEach((v) => vignetteParameters.push(v.systemKey));

            return this.combinedOrderRoute + '/' + vignetteParameters;
        },
        firstOutwardRoute() {
            return this.routes.outward.length > 0 ? [this.routes.outward[0]] : [];
        },
        requiredProducts() {
            const arr = [];

            if (this.products.length <= 0 || this.showCalendarAsResult) {
                return arr;
            }

            for (const key in this.products) {
                if (!(this.products[key] instanceof Array) || this.products[key].length <= 0) {
                    continue;
                }

                this.products[key].forEach((product) => {
                    const originUrlPath = product.origin_url_path[this.$language] ?? product.origin_url_path['en'];
                    let url = `https://${product.domain}/${originUrlPath}`;
                    let name = this.$t(`directions.advert_${product.systemKey.slice(-2)}`);

                    if (product.type.toLowerCase() === 'route_toll') {
                        const translatedName = product.names[this.$language] ?? product.names['en'];
                        name = `${this.$t('directions.requires_vignette_special')}<br>${translatedName}`;
                    } else if (this.design === 'homepage') {
                        url = `${url}?utm_source=dvoat&utm_medium=routeplannerhome&utm_campaign=routeplannerhome#${this.$t('shop.productAnchor')}`;
                    } else {
                        url = `${url}#${this.$t('shop.productAnchor')}`;
                    }

                    arr.push({
                        productUrl: url,
                        productName: name,
                        countryFlag: product.flag,
                    });
                });
            }

            return arr;
        },

        requireMultipleCountries() {
            if (this.products['vignettes'].length < 2) {
                return false;
            }

            return this.products['vignettes'].some((val) => val.domain !== this.products['vignettes'][0].domain);
        },

        currentRoute() {
            return this.routes.outward.find((route) => route.id === this.activeRoute);
        },

        currentBackRoute() {
            return this.routes.back.find((route) => route.id === this.activeBackRoute);
        },

        preselectRequiredSystems() {
            if (this.products.length <= 0 || !this.showCalendarAsResult) {
                return [];
            }

            const allSystemKeys = [];

            const routes = [this.currentRoute];
            if (this.currentBackRoute) {
                routes.push(this.currentBackRoute);
            }

            for (const route of routes) {
                const systemKeys = new Set();

                const countryChanges = Object.values(route.countryChanges).toSorted(
                    (a, b) => parseInt(a[0]) - parseInt(b[0])
                );
                const products = Object.values(route.tollProductRequirements['vignettes']).toSorted((a, b) => {
                    return countryChanges.indexOf(a.system.country) - countryChanges.indexOf(b.system.country);
                });

                for (const product of products) {
                    if (systemKeys.has(product.systemKey)) continue;
                    systemKeys.add(product.systemKey);
                }

                allSystemKeys.push(...systemKeys);
            }

            return allSystemKeys;
        },

        infos() {
            const infos = [];

            if (typeof this.products.infos === 'object' && Object.keys(this.products.infos).length > 0) {
                for (const country in this.products.infos) {
                    const productInfos = this.products.infos[country];
                    let link = productInfos[this.$i18n.fallbackLocale] || productInfos.en || '';
                    if (
                        typeof productInfos[this.$i18n.locale] === 'string' &&
                        productInfos[this.$i18n.locale].trim().length > 0
                    ) {
                        link = productInfos[this.$i18n.locale];
                    }

                    link = link || '#';
                    infos.push({
                        country: {
                            name: country,
                            label: this.$t('countries.' + country.trim().replace(/\s+/, '_').toLowerCase()),
                        },
                        content: link,
                        asLink: (link.match(/^http(s?):\/\//) || []).length > 0,
                    });
                }
            }

            return infos;
        },
    },

    props: {
        status: {
            type: Object,
            required: true,
            default() {
                return {
                    initial: true,
                    ready: false,
                    loading: false,
                    failed: false,
                    hasRouteBack: false,
                };
            },
        },
        routes: {
            type: Object,
            required: true,
            default() {
                return {
                    outward: [],
                    back: [],
                };
            },
        },
        productRoute: {
            type: String,
            required: true,
        },
        products: {
            type: Object,
            required: true,
            default() {
                return {
                    vignettes: [],
                    routeTolls: [],
                    infos: {
                        any: {
                            en: '#',
                            de: '#',
                        },
                    },
                };
            },
        },
        activeRoute: {
            type: [String, Number],
            required: false,
        },
        activeBackRoute: {
            type: [String, Number],
            required: false,
        },
        design: {
            type: String,
            required: false,
        },
        routeplanerShowProducts: {
            type: Boolean,
            require: false,
        },
        combinedOrderRoute: {
            type: String,
            require: false,
        },
        showCalendarAsResult: {
            type: Boolean,
            require: false,
            default: false,
        },
        maxValidityStart: {
            type: String,
            required: false,
        },
        maxValidityEnd: {
            type: String,
            required: false,
        },
    },
};
</script>
